import { Store } from '@ngxs/store';
import { FetchJointAccountRequests, RefreshActiveAccountDetails } from '../../actions/savings-account.action';
import { ApiLoadingState, ApiActions } from '../../state/api-loading.state';
import { SavingsAccountState } from '../../state/savings-account.state';
import { OpenBankingProvider, PaymentProvider } from '@finxone-platform/shared/sys-config-types';
import { ProgressBarState } from '../../state/progress-bar.state';
import { ProjectSettingsState } from '../../state/project-settings.state';
import { getOpenBankingProvider } from '../project-settings.utils';
import { ConfigService } from '../../services/config-service/config-service.service';

/**
 * Dispatches the action to refresh the active savings account details
 * This was added because the savings account details were not being refreshed
 * when the user navigates to the savings account details page
 * Previously it was handled by the `savingsAccountsPresent` flag in the model builder
 * but it caused issues when we get paginated responses from the API
 * because we dispatched the action always with page 1
 * So if the selected account was present on a different page, it would not be fetched
 * and the selected account would get overwritten with the first account from the first page
 * @param store - The NgRx store instance
 */
export function dispatchActiveSavingsAccountDetailsFetch(store: Store) {
  const existingSavingsState = store.selectSnapshot(SavingsAccountState.getAccounts);
  const activeId = existingSavingsState?.activeAccountId;
  const isApiLoading = store.selectSnapshot(
    ApiLoadingState.getApiState(ApiActions.getSavingsAccountDetails),
  )?.isLoading;
  if (activeId && !isApiLoading) {
    store.dispatch(new RefreshActiveAccountDetails(activeId));
  }
}

/**
 * Dispatch action to fetch pending joint account requests if provider is SaasCada
 * @param store - The NgRx store instance
 */
export function getPendingJointAccountRequests(store: Store) {
  const provider = store.selectSnapshot(ProjectSettingsState.getProjectServiceProvider);
  if (provider !== PaymentProvider.SAASCADA) return;
  const isLoading = store
    .selectSnapshot(ProgressBarState.getProgressBarStack)
    ?.some((item) => item.uniqueId === 'FetchJointAccountRequests');
  if (!isLoading) {
    store.dispatch(new FetchJointAccountRequests());
  }
}

/**
 * Determines whether the selected PISP institution should be resolved into the Handlebars model.
 * GoCardless requires the custom payment page flag; all other open-banking providers (NatWest PayIt,
 * Yapily, Bottomline, universal-sandbox) use the redirect flow and therefore need the institution.
 */
export function isPispInstitutionDisplayEnabled(store: Store, configService?: ConfigService): boolean {
  const openBankingProvider = getOpenBankingProvider(store);

  const redirectFlowProviders: OpenBankingProvider[] = [
    OpenBankingProvider.NATWEST_PAYIT,
    OpenBankingProvider.YAPILY,
    OpenBankingProvider.BOTTOMLINE,
    OpenBankingProvider.UNIVERSAL_SANDBOX,
  ];
  if (redirectFlowProviders.includes(openBankingProvider)) return true;

  if (
    openBankingProvider === OpenBankingProvider.GOCARDLESS &&
    !!configService?.config?.goCardlessEnabledCustomPaymentPages
  ) {
    return true;
  }

  return false;
}
